import { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { OrderTypeToggle } from './OrderTypeToggle';
import { useTranslation } from '@Src/providers/TranslationProvider';
import { useAppSelector } from '@Src/hooks/useAppSelector';
import { EditOrderSettingsDialog } from './EditOrderSettingsDialog';
import { OrderSettingDetails } from './OrderSettingDetails';
import { useOrderSettingSetup } from '@Src/hooks/useOrderSettingSetup';
import { type DeliveryFlowStepType } from './EditOrderSettingsDialog/DeliverySettings';
import Grid from '@mui/material/Grid';
import { ServiceUnavailable } from './ServiceUnavailable';
import { useAppDispatch } from '@Src/hooks/useAppDispatch';
import { setIsCollection } from '@Src/slices/orderSettingSlice';

type StlyledContainerProps = {
  isCollection: boolean;
  hasUserDeliveryAddress: boolean;
  hasSelectedDeliveryStore: boolean;
};
const StyledContainer = styled('div')<{
  ownerState: StlyledContainerProps;
}>(
  ({
    ownerState: {
      isCollection,
      hasUserDeliveryAddress,
      hasSelectedDeliveryStore,
    },
    theme,
  }) => ({
    minWidth: isCollection ? '100%' : '560px',
    width:
      isCollection || (hasUserDeliveryAddress && hasSelectedDeliveryStore)
        ? '100%'
        : '47%',
    [theme.breakpoints.down('sm')]: {
      minWidth: '100%',
    },
  }),
);

const StyledToggleContainer = styled(Grid)<{
  ownerState: { isToggleHidden: boolean };
}>(({ theme, ownerState }) => ({
  flexDirection: 'row',
  visibility: ownerState.isToggleHidden ? 'hidden' : 'visible',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

export function OrderSettings(): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<{
    isOpen: boolean;
    startStep?: DeliveryFlowStepType;
  }>({ isOpen: false });

  const dispatch = useAppDispatch();
  const { translate } = useTranslation();
  const theme = useTheme();

  const isCollection = useAppSelector(
    (state) => state.orderSettings.isCollection,
  );

  useOrderSettingSetup();

  const selectedCollectionStore = useAppSelector(
    (state) => state.collectionStores.selectedCollectionStore,
  );
  const selectedDeliveryAddress = useAppSelector(
    (state) => state.deliveryAddresses.selectedDeliveryAddress,
  );
  const selectedDeliveryStore = useAppSelector(
    (state) => state.deliveryStores.selectedDeliveryStore,
  );
  const isDeliveryStoresFetched = useAppSelector(
    (state) => state.deliveryStores.fetched,
  );
  const orderUrl = useAppSelector((state) => state.orderUrl.path);

  // stores list
  const deliveryStoresList = useAppSelector(
    (state) => state.deliveryStores.deliveryStoreList,
  );
  const collectionStoresList = useAppSelector(
    (state) => state.collectionStores.collectionStoreList,
  );

  const isCollectionUnavailable =
    selectedCollectionStore &&
    !selectedCollectionStore?.IsOpen &&
    !selectedCollectionStore?.AcceptPreOrders;

  const isDeliveryUnavailable =
    selectedDeliveryStore &&
    !selectedDeliveryStore?.IsOpen &&
    !selectedDeliveryStore?.AcceptPreOrders;

  const hasStores =
    !!deliveryStoresList.length || !!collectionStoresList.length;

  // #region common store props
  const selectedStore = isCollection
    ? selectedCollectionStore
    : selectedDeliveryStore;

  const { AcceptPreOrders, DistanceKm, IsOpen, PhysicalRestaurantName } =
    selectedStore || {};

  let statusMessage = IsOpen ? translate('Open') : translate('Closed');
  if (isCollection && DistanceKm) {
    statusMessage += ` • ${DistanceKm}km`;
  }
  // #endregion

  /** Only runs on page load */
  const autoSwitchCollectionIfDeliveryUnavailable = (): void => {
    if (isDeliveryStoresFetched) {
      if (!isCollection && !isCollectionUnavailable) {
        if (!deliveryStoresList.length) {
          dispatch(setIsCollection(true));
          return;
        }
        // TODO: we might not want to switch to collection in this case. For now we show the address box
        // if (deliveryStoresList.length && !selectedDeliveryStore) {
        //   dispatch(setIsCollection(true));
        //   return;
        // }
        if (deliveryStoresList.length && isDeliveryUnavailable) {
          dispatch(setIsCollection(true));
        } else {
          dispatch(setIsCollection(false));
        }
      } else {
        dispatch(setIsCollection(false));
      }
    }
  };

  useEffect(() => {
    autoSwitchCollectionIfDeliveryUnavailable();
  }, [isDeliveryStoresFetched]);

  const isToggleHidden =
    !deliveryStoresList.length || !collectionStoresList.length;

  return (
    <>
      {hasStores && (
        <StyledContainer
          ownerState={{
            isCollection,
            hasUserDeliveryAddress: !!selectedDeliveryAddress,
            hasSelectedDeliveryStore: !!selectedDeliveryStore,
          }}
        >
          <StyledToggleContainer container ownerState={{ isToggleHidden }}>
            <OrderTypeToggle
              padding={theme.spacing(1.5)}
              collectionDisabled={isCollectionUnavailable}
            />

            {isCollection && isDeliveryUnavailable && (
              <ServiceUnavailable
                headerText={translate('Delivery_currently_unavailable')}
                message={translate('Check_back_later_or_order_for_collection')}
              />
            )}
            {!isCollection && isCollectionUnavailable && (
              <ServiceUnavailable
                headerText={translate('Collection_currently_unavailable')}
                message={translate('Check_back_later_or_order_for_delivery')}
              />
            )}
          </StyledToggleContainer>

          <OrderSettingDetails
            collectionStoresLength={collectionStoresList?.length}
            deliveryStoresLength={deliveryStoresList?.length}
            hasSelectedDeliveryStore={!!selectedDeliveryStore}
            hasUserDeliveryAddress={!!selectedDeliveryAddress}
            locationName={
              isCollection
                ? PhysicalRestaurantName || ''
                : selectedDeliveryAddress?.TwoLinesDisplay?.[0] || ''
            }
            orderSetting={isCollection ? 'Collect' : 'Deliver'}
            orderUrl={orderUrl}
            setIsDialogOpen={setIsDialogOpen}
            statusMessage={statusMessage}
            storeOpen={IsOpen || false}
            storeAcceptsPreorders={AcceptPreOrders || false}
          />
        </StyledContainer>
      )}
      <EditOrderSettingsDialog
        isOpen={isDialogOpen.isOpen}
        startStep={isDialogOpen.startStep}
        onClose={() => setIsDialogOpen({ isOpen: false })}
      />
    </>
  );
}
